import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ExcelJS from 'exceljs'
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { DevanningPlan, DevanningPlanItem } from '../types'
import { inputCls, btnPrimary, btnSecondary, fmtDate } from '../types'
import { NotePDF } from '../Inbound/NotePDF'
import type { NoteData, NoteItem } from '../Inbound/NotePDF'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import type { ColDef } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from '../ColDragHeader'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { getCompanySettings } from '../../../lib/companySettings'
import { DeliveryMethodSelect } from '../../../components/DeliveryMethodSelect'
import { fetchAllRows } from '../../../lib/fetchAllRows'

const PLAN_ITEMS_COL_DEFS = [
  { id: 'hbl',     label: 'HBL/Ref' },
  { id: 'job_ref', label: 'Job Ref' },
  { id: 'desc',    label: 'Description' },
  { id: 'nm',      label: 'N/M' },
  { id: 'method',  label: 'Method' },
  { id: 'ctns',    label: 'Ctns' },
  { id: 'wt',      label: 'Wt (kg)' },
  { id: 'cbm',     label: 'CBM' },
  { id: 'address', label: 'Delivery Address' },
  { id: 'notes',   label: 'Note' },
] as const

const PI_AUTO_WIDTHS = computeAutoFitWidths(PLAN_ITEMS_COL_DEFS)

// ─── Pre-Devanning Plan PDF ───────────────────────────────────────────────────

const pdfS = StyleSheet.create({
  page:       { padding: 32, fontFamily: 'Helvetica', fontSize: 9, color: '#1a1a1a' },
  docHeader:  { marginBottom: 14, paddingBottom: 10, borderBottomWidth: 1.5, borderBottomColor: '#1e293b' },
  docTitle:   { fontSize: 14, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
  docMeta:    { fontSize: 8, color: '#64748b' },
  section:    { marginBottom: 20 },
  splitHdr:   { backgroundColor: '#1e293b', padding: '7 10 5 10' },
  splitName:  { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#f8fafc', marginBottom: 3 },
  splitMeta:  { fontSize: 8, color: '#94a3b8', marginBottom: 1 },
  thead:      { flexDirection: 'row', backgroundColor: '#e2e8f0', padding: '4 8' },
  trow:       { flexDirection: 'row', padding: '3 8', borderBottomWidth: 0.5, borderBottomColor: '#f1f5f9' },
  th:         { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#475569', textTransform: 'uppercase' },
  td:         { fontSize: 8, color: '#1e293b' },
  splitFoot:  { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', padding: '4 8', backgroundColor: '#f8fafc', borderTopWidth: 1, borderTopColor: '#cbd5e1' },
  footLbl:    { fontSize: 8, color: '#64748b', marginRight: 8 },
  footVal:    { fontSize: 11, fontFamily: 'Helvetica-Bold' },
  grandFoot:  { marginTop: 18, padding: '10 12', backgroundColor: '#1e293b', flexDirection: 'row', justifyContent: 'flex-end', gap: 0 },
  gtLbl:      { fontSize: 7, color: '#94a3b8', marginBottom: 2 },
  gtVal:      { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#f8fafc' },
})

interface PdfSplit { name: string; items: DevanningPlanItem[]; ctns: number }

function PreDevanPlanPDF({ plan, splits }: { plan: DevanningPlan; splits: PdfSplit[] }) {
  const grandTotal = splits.reduce((s, g) => s + g.ctns, 0)
  return (
    <Document>
      {splits.map((split, idx) => {
        const first = split.items[0]
        const address   = first?.delivery_address || '—'
        const contact   = first?.delivery_contact || null
        const method    = first?.delivery_method  || null
        return (
          <Page key={split.name} size="A4" style={pdfS.page} break={idx > 0}>
            {/* Document header on every page */}
            <View style={pdfS.docHeader}>
              <Text style={pdfS.docTitle}>Pre-Devanning Plan — {plan.container_no}</Text>
              <Text style={pdfS.docMeta}>
                {[plan.vessel && `Vessel: ${plan.vessel}`, plan.voyage && `Voyage: ${plan.voyage}`, plan.eta_date && `ETA: ${fmtDate(plan.eta_date)}`].filter(Boolean).join('   |   ')}
              </Text>
            </View>

            {/* Devan Split header */}
            <View style={pdfS.section}>
              <View style={pdfS.splitHdr}>
                <Text style={pdfS.splitName}>{split.name}</Text>
                <Text style={pdfS.splitMeta}>{address}</Text>
                {contact && <Text style={pdfS.splitMeta}>Contact: {contact}</Text>}
                {method  && <Text style={pdfS.splitMeta}>Method: {method}</Text>}
              </View>

              {/* Table header */}
              <View style={pdfS.thead}>
                <Text style={[pdfS.th, { width: 72 }]}>JOB REF</Text>
                <Text style={[pdfS.th, { width: 38 }]}>SPLIT NO</Text>
                <Text style={[pdfS.th, { flex: 1  }]}>DESCRIPTION</Text>
                <Text style={[pdfS.th, { width: 70 }]}>N/M</Text>
                <Text style={[pdfS.th, { width: 58 }]}>POSTCODE</Text>
                <Text style={[pdfS.th, { width: 36, textAlign: 'right' }]}>CTNS</Text>
              </View>

              {/* Rows */}
              {split.items.map((item, i) => (
                <View key={item.id} style={[pdfS.trow, i % 2 === 1 ? { backgroundColor: '#f8fafc' } : {}]}>
                  <Text style={[pdfS.td, { width: 72, fontFamily: 'Helvetica-Bold', fontSize: 8 }]}>{item.client_ref || item.hbl_no || '—'}</Text>
                  <Text style={[pdfS.td, { width: 38, fontFamily: 'Helvetica-Oblique', fontSize: 7, color: '#475569' }]}>{item.split_no || '—'}</Text>
                  <Text style={[pdfS.td, { flex: 1,  fontSize: 7 }]}>{item.description || '—'}</Text>
                  <Text style={[pdfS.td, { width: 70, fontSize: 7, color: '#475569' }]}>{item.system_ref || '—'}</Text>
                  <Text style={[pdfS.td, { width: 58, fontSize: 7 }]}>{item.delivery_postcode || '—'}</Text>
                  <Text style={[pdfS.td, { width: 36, textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>{item.expected_cartons}</Text>
                </View>
              ))}

              {/* Split footer */}
              <View style={pdfS.splitFoot}>
                <Text style={pdfS.footLbl}>TOTAL CTNS</Text>
                <Text style={pdfS.footVal}>{split.ctns}</Text>
              </View>
            </View>

            {/* Grand total on last page */}
            {idx === splits.length - 1 && (
              <View style={pdfS.grandFoot}>
                <View>
                  <Text style={pdfS.gtLbl}>GRAND TOTAL CTNS</Text>
                  <Text style={pdfS.gtVal}>{grandTotal}</Text>
                </View>
              </View>
            )}
          </Page>
        )
      })}
    </Document>
  )
}

// ─── Move to Planned Dialog ───────────────────────────────────────────────────
// Creates the devanning record + copies plan items → devanning_items (with group_name)

function MoveToPlannedDialog({ plan, jobRef, warehouseId, onDone, onClose }: {
  plan: DevanningPlan
  jobRef: string | null
  warehouseId: string
  onDone: (devanningId: string) => void
  onClose: () => void
}) {
  const [plannedDate, setPlannedDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!plannedDate) { setError('Please set planned devan date before moving to Devannings.'); return }
    setSaving(true); setError(null)
    try {
      // 1. Mark plan as planned
      const { error: planErr } = await supabase.from('devanning_plans').update({
        status: 'planned',
        planned_devan_at: plannedDate,
      }).eq('id', plan.id)
      if (planErr) throw planErr

      // Sync devanning_schedule so WarehouseList Today's Schedule stays in step
      if (jobRef) {
        await supabase.from('devanning_schedule')
          .update({ planned_devan_at: plannedDate })
          .eq('job_ref', jobRef)
      }

      // 2. UPSERT devanning (idempotent — Generate Plan may have pre-created it)
      const { data: existingDev } = await supabase
        .from('devannings').select('id').eq('plan_id', plan.id).maybeSingle()
      let devId: string
      if (existingDev) {
        devId = existingDev.id
      } else {
        const { data: newDev, error: devErr } = await supabase.from('devannings').insert({
          plan_id: plan.id, warehouse_id: warehouseId,
          container_no: plan.container_no, status: 'awaiting',
        }).select('id').single()
        if (devErr) throw devErr
        devId = newDev.id
      }

      // 3. Replace devanning_items from plan_items (always fresh sync)
      const { data: planItems } = await supabase.from('devanning_plan_items')
        .select('*').eq('plan_id', plan.id).order('sort_order')
      if (planItems && planItems.length > 0) {
        await supabase.from('devanning_items').delete().eq('devanning_id', devId)
        await supabase.from('devanning_items').insert(
          planItems.map((pi: DevanningPlanItem) => ({
            devanning_id:       devId,
            plan_item_id:       pi.id,
            hbl_no:             pi.hbl_no,
            description:        pi.description ?? null,
            customer_id:        pi.customer_id,
            delivery_address:   pi.delivery_address,
            delivery_method:    pi.delivery_method ?? null,
            delivery_method_id: pi.delivery_method_id ?? null,
            expected_cartons:   pi.expected_cartons,
            expected_weight_kg: pi.expected_weight_kg,
            expected_cbm:       pi.expected_cbm,
            actual_cartons:     0,
            pallet_count:       0,
            pallets_qty:        0,
            pallets_detail:     [],
            condition:          'good',
            split_no:           pi.split_no ?? null,
            system_ref:         pi.system_ref ?? null,
            group_name:         pi.group_name ?? null,
          }))
        )
      }
      onDone(devId)
    } catch (err: any) {
      setError(err.message || 'Failed')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Move to Planned</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Marks the plan as <strong>Planned</strong> and automatically creates a Devanning record in the Devannings tab, ready for warehouse execution.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Planned Devan Date &amp; Time <span className="text-red-500">*</span>
            </label>
            <input type="datetime-local" className={inputCls}
              value={plannedDate} onChange={e => setPlannedDate(e.target.value)} />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
            <button type="button" onClick={handleConfirm} disabled={saving || !plannedDate}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Moving…' : 'Confirm — Move to Devannings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Plan Item Modal (Edit / Add) ────────────────────────────────────────────

function PlanItemModal({ item, groupName, onSave, onClose }: {
  item: DevanningPlanItem | null
  groupName: string | null
  onSave: (data: Record<string, unknown>) => Promise<void>
  onClose: () => void
}) {
  const isAdd = item === null
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    client_ref:          item?.client_ref          ?? '',
    hbl_no:              item?.hbl_no              ?? '',
    split_no:            item?.split_no            ?? '',
    system_ref:          item?.system_ref          ?? '',
    description:         item?.description         ?? '',
    expected_cartons:    item?.expected_cartons    ?? 0,
    expected_weight_kg:  item?.expected_weight_kg  ?? '',
    expected_cbm:        item?.expected_cbm        ?? '',
    delivery_address:    item?.delivery_address    ?? '',
    delivery_postcode:   item?.delivery_postcode   ?? '',
    delivery_contact:    item?.delivery_contact    ?? '',
    delivery_method:     item?.delivery_method     ?? '',
    delivery_method_id:  item?.delivery_method_id  ?? (null as string | null),
  })
  const set = (k: string, v: string | number) => setForm(p => ({ ...p, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      await onSave({
        client_ref:         form.client_ref         || null,
        hbl_no:             form.hbl_no             || null,
        split_no:           form.split_no           || null,
        system_ref:         form.system_ref         || null,
        description:        form.description        || null,
        expected_cartons:   Number(form.expected_cartons) || 0,
        expected_weight_kg: form.expected_weight_kg !== '' ? Number(form.expected_weight_kg) : null,
        expected_cbm:       form.expected_cbm       !== '' ? Number(form.expected_cbm)       : null,
        delivery_address:   form.delivery_address   || null,
        delivery_postcode:  form.delivery_postcode  || null,
        delivery_contact:   form.delivery_contact   || null,
        delivery_method:    form.delivery_method    || null,
        delivery_method_id: form.delivery_method_id,
        group_name:         isAdd ? groupName : item?.group_name ?? null,
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{isAdd ? 'Add Plan Item' : 'Edit Plan Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Ref (Client Ref)</label>
              <input className={inputCls} value={form.client_ref} onChange={e => set('client_ref', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HBL No.</label>
              <input className={inputCls} value={form.hbl_no} onChange={e => set('hbl_no', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Split No.</label>
              <input className={inputCls} value={form.split_no} onChange={e => set('split_no', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">N/M (System Ref)</label>
              <input className={inputCls} value={form.system_ref} onChange={e => set('system_ref', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Cartons</label>
              <input type="number" min="0" className={inputCls} value={form.expected_cartons} onChange={e => set('expected_cartons', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Weight (kg)</label>
              <input type="number" min="0" step="0.001" className={inputCls} value={form.expected_weight_kg} onChange={e => set('expected_weight_kg', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CBM</label>
              <input type="number" min="0" step="0.001" className={inputCls} value={form.expected_cbm} onChange={e => set('expected_cbm', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Address</label>
              <input className={inputCls} value={form.delivery_address} onChange={e => set('delivery_address', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Postcode</label>
              <input className={inputCls} value={form.delivery_postcode} onChange={e => set('delivery_postcode', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Contact</label>
              <input className={inputCls} value={form.delivery_contact} onChange={e => set('delivery_contact', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Method</label>
              <DeliveryMethodSelect methodId={form.delivery_method_id} freeText={form.delivery_method}
                onChange={v => setForm(p => ({ ...p, delivery_method_id: v.delivery_method_id, delivery_method: v.delivery_method || '' }))} />
            </div>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : isAdd ? 'Add Item' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Plan Item Split Modal ────────────────────────────────────────────────────

function PlanItemSplitModal({ item, onSplit, onClose }: {
  item: DevanningPlanItem
  onSplit: (a: Record<string, unknown>, b: Record<string, unknown>) => Promise<void>
  onClose: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const origCtns = item.expected_cartons
  const [partA, setPartA] = useState({
    split_no:          (item.split_no ?? '') + 'A',
    expected_cartons:  Math.floor(origCtns / 2),
    delivery_address:  item.delivery_address ?? '',
    delivery_postcode: item.delivery_postcode ?? '',
  })
  const [partB, setPartB] = useState({
    split_no:          (item.split_no ?? '') + 'B',
    expected_cartons:  origCtns - Math.floor(origCtns / 2),
    delivery_address:  item.delivery_address ?? '',
    delivery_postcode: item.delivery_postcode ?? '',
  })

  const setA = (k: string, v: string | number) => setPartA(p => ({ ...p, [k]: v }))
  const setB = (k: string, v: string | number) => setPartB(p => ({ ...p, [k]: v }))

  const totalOk = Number(partA.expected_cartons) + Number(partB.expected_cartons) === origCtns

  const handleConfirm = async () => {
    if (!totalOk) return
    setSaving(true); setError(null)
    try {
      await onSplit(
        { ...partA, expected_cartons: Number(partA.expected_cartons) },
        { ...partB, expected_cartons: Number(partB.expected_cartons) },
      )
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to split')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Split Plan Item</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold">Original:</span>{' '}
            {item.client_ref || item.hbl_no || '—'}{item.description ? ` — ${item.description}` : ''}{' '}
            <span className="font-mono font-bold">{origCtns} ctns</span>
          </div>
          <p className={`text-xs text-center font-medium ${totalOk ? 'text-green-600' : 'text-orange-600'}`}>
            Part A ({partA.expected_cartons}) + Part B ({partB.expected_cartons}) = {Number(partA.expected_cartons) + Number(partB.expected_cartons)} / {origCtns} required
          </p>
          <div className="grid grid-cols-2 gap-6">
            {/* Part A */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-indigo-700 dark:text-indigo-300">Part A</h3>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Split No.</label>
                <input className={inputCls} value={partA.split_no} onChange={e => setA('split_no', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cartons</label>
                <input type="number" min="0" className={inputCls} value={partA.expected_cartons} onChange={e => setA('expected_cartons', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delivery Address</label>
                <input className={inputCls} value={partA.delivery_address} onChange={e => setA('delivery_address', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Postcode</label>
                <input className={inputCls} value={partA.delivery_postcode} onChange={e => setA('delivery_postcode', e.target.value)} />
              </div>
            </div>
            {/* Part B */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-indigo-700 dark:text-indigo-300">Part B</h3>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Split No.</label>
                <input className={inputCls} value={partB.split_no} onChange={e => setB('split_no', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cartons</label>
                <input type="number" min="0" className={inputCls} value={partB.expected_cartons} onChange={e => setB('expected_cartons', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delivery Address</label>
                <input className={inputCls} value={partB.delivery_address} onChange={e => setB('delivery_address', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Postcode</label>
                <input className={inputCls} value={partB.delivery_postcode} onChange={e => setB('delivery_postcode', e.target.value)} />
              </div>
            </div>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
            <button type="button" disabled={!totalOk || saving} onClick={handleConfirm} className={btnPrimary}>
              {saving ? 'Splitting…' : 'Confirm Split'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Group Card ───────────────────────────────────────────────────────────────

function MasterCheckbox({ groupItems, selectedItems, onSelectAll }: {
  groupItems: { id: string }[]
  selectedItems: Set<string>
  onSelectAll: (ids: string[], allSelected: boolean) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const ids = groupItems.map(i => i.id)
  const selectedCount = ids.filter(id => selectedItems.has(id)).length
  const allSelected = ids.length > 0 && selectedCount === ids.length
  const partial = selectedCount > 0 && !allSelected

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial
  }, [partial])

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={allSelected}
      onChange={() => onSelectAll(ids, allSelected)}
      className="w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer"
    />
  )
}

interface GroupEntry {
  name: string | null
  items: DevanningPlanItem[]
  ctns: number
  wt: number
  cbm: number
}

interface Agent { id: string; code: string; name: string; customer_type: string | null }

function GroupCard({ group, companies: _companies, onUpdateAgent: _onUpdateAgent, index, onEdit, onDelete, onSplit, onAdd, allGroupNames, selectedItems, onToggleSelect, onMoveItem, isReadOnly, itemVisibleCols, itemColOrder, itemSensors, handleItemColDragEnd, getColW, onResizeEnd, onResizeReset, onRename, onSelectAll, onDraftNote }: {
  group: GroupEntry
  companies: Agent[]
  onUpdateAgent: (itemId: string, customerId: string | null) => void
  index: number
  onEdit: (item: DevanningPlanItem) => void
  onDelete: (item: DevanningPlanItem) => void
  onSplit: (item: DevanningPlanItem) => void
  onAdd: (groupName: string | null) => void
  allGroupNames: string[]
  selectedItems: Set<string>
  onToggleSelect: (id: string, checked: boolean) => void
  onMoveItem: (itemId: string, targetGroup: string | null) => void
  isReadOnly?: boolean
  itemVisibleCols: ColDef[]
  itemColOrder: string[]
  itemSensors: ReturnType<typeof useSensors>
  handleItemColDragEnd: (e: DragEndEvent) => void
  getColW: (id: string) => number
  onResizeEnd: (id: string, w: number) => void
  onResizeReset: (id: string) => void
  onRename: (oldName: string, newName: string) => void
  onSelectAll: (ids: string[], allSelected: boolean) => void
  onDraftNote?: (group: GroupEntry, type: 'collection' | 'delivery') => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [moveOpenId, setMoveOpenId] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [renameVal, setRenameVal] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  // Strip any verbose suffix (old format: "Devan Split N — Delivery: ...") and
  // normalize the pre-#283 "Devan Split N" naming to "Group N" for display.
  const label = (group.name ?? 'Unassigned').replace(/\s*[—–-].*$/, '').trim().replace(/^Devan Split\s+/i, 'Group ')
  const isUnassigned = group.name === null

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isUnassigned
        ? 'border-gray-200 dark:border-gray-700'
        : 'border-indigo-200 dark:border-indigo-700'
    }`}>
      {/* Group header — always visible */}
      <div
        onClick={() => setExpanded(e => !e)}
        className={`w-full flex items-center justify-between px-5 py-3.5 text-left transition-colors cursor-pointer ${
          isUnassigned
            ? 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
            : 'bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {!isReadOnly && (
            <span onClick={e => e.stopPropagation()}>
              <MasterCheckbox
                groupItems={group.items}
                selectedItems={selectedItems}
                onSelectAll={onSelectAll}
              />
            </span>
          )}
          <span className={`flex-shrink-0 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center ${
            isUnassigned ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300' : 'bg-indigo-200 dark:bg-indigo-700 text-indigo-700 dark:text-indigo-200'
          }`}>{index + 1}</span>
          {renaming ? (
            <input
              ref={renameInputRef}
              value={renameVal}
              onChange={e => setRenameVal(e.target.value)}
              onClick={e => e.stopPropagation()}
              onBlur={() => { const v = renameVal.trim(); if (v && v !== group.name) onRename(group.name!, v); setRenaming(false) }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.currentTarget.blur() }
                if (e.key === 'Escape') { setRenaming(false) }
                e.stopPropagation()
              }}
              className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 bg-transparent border-b border-indigo-400 focus:outline-none w-40"
              autoFocus
            />
          ) : (
            <span className={`font-semibold text-sm ${isUnassigned ? 'text-gray-500 italic' : 'text-indigo-800 dark:text-indigo-200'}`}>
              {label}
            </span>
          )}
          <span className="text-xs text-gray-400 flex-shrink-0">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
          {!isUnassigned && !isReadOnly && !renaming && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setRenameVal(group.name ?? ''); setRenaming(true) }}
              className="text-xs text-blue-500 hover:text-blue-700 font-medium flex-shrink-0"
            >
              Rename
            </button>
          )}
          {!isUnassigned && (() => {
            const first = group.items[0]
            const method   = first?.delivery_method   || null
            const postcode = first?.delivery_postcode || null
            if (!method && !postcode) return null
            return (
              <span className="hidden sm:flex items-center gap-1.5 text-xs font-mono">
                {method   && <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">{method}</span>}
                {postcode && <span className="text-gray-500 dark:text-gray-400">{postcode}</span>}
              </span>
            )
          })()}
        </div>
        <div className="flex items-center gap-6">
          <div className="flex gap-4 text-sm">
            <span className="font-semibold font-mono text-gray-800 dark:text-gray-100">{group.ctns} ctns</span>
            {group.wt > 0 && <span className="text-gray-400 text-xs">{group.wt.toFixed(1)} kg</span>}
            {group.cbm > 0 && <span className="text-gray-400 text-xs">{group.cbm.toFixed(3)} cbm</span>}
          </div>
          {onDraftNote && !isUnassigned && (
            <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => onDraftNote(group, 'collection')}
                className="px-2 py-1 text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 rounded-md hover:bg-teal-200 font-medium whitespace-nowrap">
                Draft CN
              </button>
              <button
                onClick={() => onDraftNote(group, 'delivery')}
                className="px-2 py-1 text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 rounded-md hover:bg-violet-200 font-medium whitespace-nowrap">
                Draft DN
              </button>
            </div>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded items table */}
      {expanded && (
        <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm divide-y divide-gray-100 dark:divide-gray-800">
            <DndContext sensors={itemSensors} collisionDetection={closestCenter} onDragEnd={handleItemColDragEnd}>
              <SortableContext items={itemColOrder} strategy={horizontalListSortingStrategy}>
                <thead className="bg-white dark:bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 w-8" />
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">#</th>
                    {itemVisibleCols.map(col => (
                      <SortableColHeader key={col.id} id={col.id}
                        style={{ width: getColW(col.id), minWidth: '50px' }}
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap"
                        onResizeEnd={w => onResizeEnd(col.id, w)}
                        onResizeReset={() => onResizeReset(col.id)}>
                        {col.label}
                      </SortableColHeader>
                    ))}
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
              </SortableContext>
            </DndContext>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
              {group.items.map((item, i) => {
                const itemCells: Record<string, React.ReactNode> = {
                  hbl:     <td key="hbl"     className="px-3 py-2 font-mono text-xs">{item.hbl_no || '—'}</td>,
                  job_ref: <td key="job_ref" className="px-3 py-2 font-mono text-xs text-blue-700 dark:text-blue-400">{item.client_ref || '—'}</td>,
                  desc:    <td key="desc"    className="px-3 py-2 text-xs max-w-[180px] truncate">{item.description || '—'}</td>,
                  nm:      <td key="nm"      className="px-3 py-2 text-xs text-gray-500 font-mono">{item.system_ref || '—'}</td>,
                  method:  <td key="method"  className="px-3 py-2 text-xs text-gray-500">{item.delivery_method || '—'}</td>,
                  ctns:    <td key="ctns"    className="px-3 py-2 text-center font-mono">{item.expected_cartons}</td>,
                  wt:      <td key="wt"      className="px-3 py-2 text-center text-gray-500 text-xs">{item.expected_weight_kg ?? '—'}</td>,
                  cbm:     <td key="cbm"     className="px-3 py-2 text-center text-gray-500 text-xs">{item.expected_cbm ?? '—'}</td>,
                  address: <td key="address" className="px-3 py-2 text-xs text-gray-500 max-w-[200px] break-words">{item.delivery_address || '—'}</td>,
                  notes:   <td key="notes"   className="px-3 py-2 text-xs text-gray-500 max-w-[160px] break-words">{item.notes || '—'}</td>,
                }
                return (
                <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${selectedItems.has(item.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" checked={selectedItems.has(item.id)}
                      onChange={e => onToggleSelect(item.id, e.target.checked)}
                      className="w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer" />
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                  {itemVisibleCols.map(col => itemCells[col.id])}
                  <td className="px-2 py-1 whitespace-nowrap">
                    {!isReadOnly && (
                      <div className="flex items-center gap-1">
                        {/* Move */}
                        <div className="relative">
                          <button type="button" title="Move to split"
                            onClick={e => { e.stopPropagation(); setMoveOpenId(id => id === item.id ? null : item.id) }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-indigo-600">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          {moveOpenId === item.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setMoveOpenId(null)} />
                              <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl py-1 min-w-[160px] max-h-56 overflow-y-auto">
                                {allGroupNames.filter(n => n !== group.name).map(n => (
                                  <button key={n} type="button" onClick={() => { onMoveItem(item.id, n); setMoveOpenId(null) }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    {n}
                                  </button>
                                ))}
                                {group.name !== null && (
                                  <button type="button" onClick={() => { onMoveItem(item.id, null); setMoveOpenId(null) }}
                                    className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                                    Unassigned
                                  </button>
                                )}
                                <div className="border-t border-gray-100 dark:border-gray-700 my-0.5" />
                                <button type="button" onClick={() => {
                                  const max = allGroupNames.reduce((m, n) => Math.max(m, parseInt(n.match(/(\d+)$/)?.[1] ?? '0')), 0)
                                  onMoveItem(item.id, `Devan Split ${max + 1}`)
                                  setMoveOpenId(null)
                                }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                                  + New Split
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Edit */}
                        <button type="button" onClick={() => onEdit(item)} title="Edit"
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-blue-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
                          </svg>
                        </button>
                        {/* Split */}
                        <button type="button" onClick={() => onSplit(item)} title="Split">
                          <span className="text-xs text-gray-400 hover:text-indigo-600 px-0.5">✂</span>
                        </button>
                        {/* Delete */}
                        <button type="button" onClick={() => onDelete(item)} title="Delete"
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
              })}
              {!isReadOnly && (
                <tr>
                  <td colSpan={itemVisibleCols.length + 3} className="px-3 py-2">
                    <button type="button" onClick={() => onAdd(group.name)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add item
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold">
              <tr>
                <td colSpan={2} className="px-3 py-2 text-right text-gray-400">Subtotal</td>
                {itemVisibleCols.map(col => {
                  if (col.id === 'ctns')    return <td key="ctns" className="px-3 py-2 text-center font-mono">{group.ctns}</td>
                  if (col.id === 'wt')      return <td key="wt"   className="px-3 py-2 text-center">{group.wt > 0 ? group.wt.toFixed(1) : '—'}</td>
                  if (col.id === 'cbm')     return <td key="cbm"  className="px-3 py-2 text-center">{group.cbm > 0 ? group.cbm.toFixed(3) : '—'}</td>
                  return <td key={col.id} />
                })}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Plan Detail ──────────────────────────────────────────────────────────────

interface LinkedDev { id: string; status: string; container_no: string }

export default function PlanDetail() {
  const { id, pid } = useParams<{ id: string; pid: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [plan, setPlan] = useState<DevanningPlan | null>(null)
  const [planJobRef, setPlanJobRef] = useState<string | null>(null)
  const [items, setItems] = useState<DevanningPlanItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showMoveToPlanned, setShowMoveToPlanned] = useState(false)
  const [companies, setCompanies] = useState<Agent[]>([])
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingExcel, setDownloadingExcel] = useState(false)
  const [draftNote, setDraftNote] = useState<{ form: NoteData; items: NoteItem[]; submitting: boolean } | null>(null)

  // Plan items column drag prefs (shared between GroupCard and flat table)
  const { order: piColOrder, hidden: piHidden, visibleCols: piVisibleCols,
    setOrder: setPiOrder, toggleHidden: piToggleHidden, reset: piReset,
    extra: piExtra, setExtra: setPiExtra } =
    useColumnPrefs('col_pref_plan_items', PLAN_ITEMS_COL_DEFS, user?.id, undefined, { widths: {} })

  const { scrollProps: piScrollProps, contentRef: piTableRef, bar: piScrollBar } = useStickyHScroll([items, piVisibleCols])
  const piSavedWidths = (piExtra.widths as Record<string, number>) ?? {}
  const getPiColW = (id: string) => piSavedWidths[id] ?? PI_AUTO_WIDTHS[id] ?? 120
  const savePiColW = (id: string, w: number) => setPiExtra({ ...piExtra, widths: { ...piSavedWidths, [id]: w } })
  const resetPiColW = (id: string) => { const n = { ...piSavedWidths }; delete n[id]; setPiExtra({ ...piExtra, widths: n }) }
  const piSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handlePiColDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oi = piColOrder.indexOf(String(active.id))
      const ni = piColOrder.indexOf(String(over.id))
      if (oi !== -1 && ni !== -1) setPiOrder(arrayMove(piColOrder, oi, ni))
    }
  }
  const [modalState, setModalState] = useState<{ type: 'edit' | 'add' | 'split'; item?: DevanningPlanItem; groupName?: string | null } | null>(null)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false)
  const [linkedDev, setLinkedDev] = useState<LinkedDev | null>(null)
  const [syncConfirmOp, setSyncConfirmOp] = useState<{ fn: () => Promise<void> } | null>(null)
  const [syncingLL, setSyncingLL] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  const handleSyncFromLL = async () => {
    if (!plan?.source_loading_list_id || !pid) return
    setSyncingLL(true); setSyncResult(null)
    try {
      const { data: ll } = await supabase
        .from('loading_lists')
        .select('parsed_data')
        .eq('id', plan.source_loading_list_id)
        .single()
      const llItems: any[] = (ll as any)?.parsed_data?.items ?? []
      if (llItems.length === 0) { setSyncResult('Loading list has no items.'); return }

      // If no plan items exist yet, do a full insert (plan was created before items were populated)
      if (items.length === 0) {
        await supabase.from('devanning_plan_items').delete().eq('plan_id', pid)
        const { error: insErr } = await supabase.from('devanning_plan_items').insert(
          llItems.map((src: any, i: number) => ({
            plan_id:            pid,
            hbl_no:             src.hbl_no             || null,
            client_ref:         src.job_ref || src.client_ref || null,
            description:        src.description        || null,
            expected_cartons:   src.expected_cartons   ?? 0,
            expected_weight_kg: src.expected_weight_kg ?? null,
            expected_cbm:       src.expected_cbm       ?? null,
            delivery_address:   src.delivery_address   || null,
            delivery_postcode:  src.delivery_postcode  || null,
            delivery_contact:   src.delivery_contact   || null,
            delivery_method:    src.delivery_method    || null,
            split_no:           src.split_no           || null,
            system_ref:         src.system_ref         || null,
            sort_order:         i,
          }))
        )
        if (insErr) throw insErr
        const { data: fresh } = await supabase
          .from('devanning_plan_items')
          .select('*, customer:customers(code, name)')
          .eq('plan_id', pid)
          .order('sort_order')
        setItems((fresh as DevanningPlanItem[]) || [])
        setSyncResult(`Inserted ${llItems.length} item${llItems.length !== 1 ? 's' : ''} from loading list.`)
        return
      }

      // Match existing plan items: by hbl_no first, then by sort_order (index)
      const byHbl = new Map<string, DevanningPlanItem>()
      const byIdx = new Map<number, DevanningPlanItem>()
      for (const pi of items) {
        if (pi.hbl_no) byHbl.set(pi.hbl_no, pi)
        byIdx.set(pi.sort_order, pi)
      }

      let updated = 0
      for (let i = 0; i < llItems.length; i++) {
        const src = llItems[i]
        const planItem = (src.hbl_no && byHbl.has(src.hbl_no))
          ? byHbl.get(src.hbl_no)!
          : byIdx.get(i) ?? null
        if (!planItem) continue

        const patch: Record<string, unknown> = {
          hbl_no:             src.hbl_no             ?? null,
          client_ref:         src.job_ref || src.client_ref || null,
          description:        src.description        ?? null,
          expected_cartons:   src.expected_cartons   ?? planItem.expected_cartons ?? 0,
          expected_weight_kg: src.expected_weight_kg ?? null,
          expected_cbm:       src.expected_cbm       ?? null,
          delivery_address:   src.delivery_address   ?? null,
          delivery_postcode:  src.delivery_postcode  ?? null,
          delivery_contact:   src.delivery_contact   ?? null,
          delivery_method:    src.delivery_method    ?? null,
          split_no:           src.split_no           ?? null,
          system_ref:         src.system_ref         ?? null,
        }
        const { error } = await supabase
          .from('devanning_plan_items').update(patch).eq('id', planItem.id)
        if (!error) updated++
      }

      // Reload items from DB
      const { data: fresh } = await supabase
        .from('devanning_plan_items')
        .select('*, customer:customers(code, name)')
        .eq('plan_id', pid)
        .order('sort_order')
      setItems((fresh as DevanningPlanItem[]) || [])
      setSyncResult(`Synced ${updated} item${updated !== 1 ? 's' : ''} from loading list.`)
    } catch (e: any) {
      setSyncResult(`Sync failed: ${e.message}`)
    } finally {
      setSyncingLL(false)
      setTimeout(() => setSyncResult(null), 4000)
    }
  }

  const fetchPlan = useCallback(async () => {
    if (!pid) return
    const [{ data: p }, { data: pi }, co, { data: dev }] = await Promise.all([
      supabase.from('devanning_plans').select('*, source_ll:loading_lists!source_loading_list_id(job_ref)').eq('id', pid).single(),
      supabase.from('devanning_plan_items').select('*, customer:customers(code, name)').eq('plan_id', pid).order('sort_order'),
      fetchAllRows<Agent>((from, to) =>
        supabase.from('customers').select('id, code, name, customer_type').eq('is_active', true).order('name').order('id').range(from, to) as never),
      supabase.from('devannings').select('id, status, container_no').eq('plan_id', pid).order('created_at', { ascending: false }),
    ])
    setPlan(p as DevanningPlan)
    setPlanJobRef((p as any)?.source_ll?.job_ref ?? (p as any)?.job_ref ?? null)
    setItems((pi as DevanningPlanItem[]) || [])
    setCompanies((co as Agent[]) || [])
    const devArr = (dev as LinkedDev[] | null) || []
    const activeDev = devArr.find(d => d.status !== 'cancelled') || devArr[0] || null
    setLinkedDev(activeDev)
    setLoading(false)
  }, [pid])

  const updateAgent = async (itemId: string, customerId: string | null) => {
    await supabase.from('devanning_plan_items').update({ customer_id: customerId }).eq('id', itemId)
    setItems(prev => prev.map(it => it.id === itemId
      ? { ...it, customer_id: customerId, customer: customerId ? (companies.find(c => c.id === customerId) ?? null) : null }
      : it
    ))
  }

  // Sync plan item changes to devanning_items (preserves pallets_detail)
  const syncToDevItem = async (planItemId: string, data: Record<string, unknown>) => {
    if (!linkedDev) return
    const syncFields: Record<string, unknown> = {}
    const allow = ['hbl_no','description','expected_cartons','expected_weight_kg','expected_cbm','delivery_address','delivery_method','delivery_method_id','split_no','system_ref','group_name','customer_id']
    for (const k of allow) { if (k in data) syncFields[k] = data[k] }
    if (Object.keys(syncFields).length > 0) {
      await supabase.from('devanning_items').update(syncFields).eq('plan_item_id', planItemId)
    }
  }

  const handleSaveItem = async (data: Record<string, unknown>) => {
    if (!plan) return
    const capturedModal = modalState

    const doSave = async () => {
      if (capturedModal?.item?.id) {
        const { error } = await supabase.from('devanning_plan_items').update(data).eq('id', capturedModal.item.id)
        if (error) throw error
        setItems(prev => prev.map(it => it.id === capturedModal.item!.id ? { ...it, ...data } as DevanningPlanItem : it))
        if (linkedDev?.status === 'awaiting') await syncToDevItem(capturedModal.item.id, data)
      } else {
        const { data: inserted, error } = await supabase
          .from('devanning_plan_items')
          .insert({ plan_id: plan.id, sort_order: items.length, ...data })
          .select('*, customer:customers(code, name)')
          .single()
        if (error) throw error
        const newItem = inserted as DevanningPlanItem
        setItems(prev => [...prev, newItem])
        if (linkedDev && ['awaiting', 'started'].includes(linkedDev.status)) {
          await supabase.from('devanning_items').insert({
            devanning_id:       linkedDev.id,
            plan_item_id:       newItem.id,
            hbl_no:             newItem.hbl_no,
            description:        newItem.description ?? null,
            customer_id:        newItem.customer_id,
            delivery_address:   newItem.delivery_address,
            delivery_method:    newItem.delivery_method ?? null,
            delivery_method_id: newItem.delivery_method_id ?? null,
            expected_cartons:   newItem.expected_cartons,
            expected_weight_kg: newItem.expected_weight_kg,
            expected_cbm:       newItem.expected_cbm,
            actual_cartons:     0,
            pallet_count:       0,
            pallets_qty:        0,
            pallets_detail:     [],
            condition:          'good',
            split_no:           newItem.split_no ?? null,
            system_ref:         newItem.system_ref ?? null,
            group_name:         newItem.group_name ?? null,
          })
        }
      }
    }

    if (linkedDev && ['awaiting', 'started'].includes(linkedDev.status)) {
      setSyncConfirmOp({ fn: doSave })
    } else {
      await doSave()
    }
  }

  const handleToggleSelect = useCallback((itemId: string, checked: boolean) => {
    setSelectedItems(prev => { const n = new Set(prev); checked ? n.add(itemId) : n.delete(itemId); return n })
  }, [])

  const handleSelectAll = useCallback((ids: string[], allSelected: boolean) => {
    setSelectedItems(prev => {
      const next = new Set(prev)
      if (allSelected) ids.forEach(id => next.delete(id))
      else ids.forEach(id => next.add(id))
      return next
    })
  }, [])

  const handleMoveItem = useCallback(async (itemId: string, targetGroup: string | null) => {
    const doMove = async () => {
      const { error } = await supabase.from('devanning_plan_items').update({ group_name: targetGroup }).eq('id', itemId)
      if (error) { alert(error.message); return }
      setItems(prev => prev.map(it => it.id === itemId ? { ...it, group_name: targetGroup } : it))
      setSelectedItems(prev => { const n = new Set(prev); n.delete(itemId); return n })
      if (linkedDev?.status === 'awaiting') {
        await supabase.from('devanning_items').update({ group_name: targetGroup }).eq('plan_item_id', itemId)
      }
    }
    if (linkedDev?.status === 'awaiting') {
      setSyncConfirmOp({ fn: doMove })
    } else {
      await doMove()
    }
  }, [linkedDev])

  const handleBulkMove = useCallback(async (targetGroup: string | null) => {
    const ids = [...selectedItems]
    const doBulkMove = async () => {
      const { error } = await supabase.from('devanning_plan_items').update({ group_name: targetGroup }).in('id', ids)
      if (error) { alert(error.message); return }
      setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, group_name: targetGroup } : it))
      setSelectedItems(new Set())
      setBulkMoveOpen(false)
      if (linkedDev?.status === 'awaiting') {
        await supabase.from('devanning_items').update({ group_name: targetGroup }).in('plan_item_id', ids)
      }
    }
    if (linkedDev?.status === 'awaiting') {
      setSyncConfirmOp({ fn: doBulkMove })
    } else {
      await doBulkMove()
    }
  }, [selectedItems, linkedDev])

  const handleDeleteItem = async (item: DevanningPlanItem) => {
    if (!window.confirm('Delete this item?')) return
    const doDelete = async () => {
      const { error } = await supabase.from('devanning_plan_items').delete().eq('id', item.id)
      if (error) { alert(error.message); return }
      setItems(prev => prev.filter(it => it.id !== item.id))
      if (linkedDev?.status === 'awaiting') {
        await supabase.from('devanning_items').delete().eq('plan_item_id', item.id)
      }
    }
    if (linkedDev?.status === 'awaiting') {
      setSyncConfirmOp({ fn: doDelete })
    } else {
      await doDelete()
    }
  }

  const handleSplitItem = async (original: DevanningPlanItem, a: Record<string, unknown>, b: Record<string, unknown>) => {
    const base = {
      plan_id:            original.plan_id,
      hbl_no:             original.hbl_no,
      client_ref:         original.client_ref,
      customer_id:        original.customer_id,
      description:        original.description,
      expected_weight_kg: original.expected_weight_kg,
      expected_cbm:       original.expected_cbm,
      delivery_contact:   original.delivery_contact,
      delivery_method:    original.delivery_method,
      delivery_method_id: original.delivery_method_id,
      system_ref:         original.system_ref,
      notes:              original.notes,
      group_name:         original.group_name,
      sort_order:         original.sort_order,
    }

    const doSplit = async () => {
      const { error: delErr } = await supabase.from('devanning_plan_items').delete().eq('id', original.id)
      if (delErr) throw delErr
      const { data: inserted, error: insErr } = await supabase
        .from('devanning_plan_items')
        .insert([{ ...base, ...a }, { ...base, ...b }])
        .select('*, customer:customers(code, name)')
      if (insErr) throw insErr
      const newItems = inserted as DevanningPlanItem[]
      setItems(prev => {
        const without = prev.filter(it => it.id !== original.id)
        return [...without, ...newItems]
      })
      if (linkedDev && ['awaiting', 'started'].includes(linkedDev.status)) {
        await supabase.from('devanning_items').delete().eq('plan_item_id', original.id)
        await supabase.from('devanning_items').insert(
          newItems.map(ni => ({
            devanning_id:       linkedDev.id,
            plan_item_id:       ni.id,
            hbl_no:             ni.hbl_no,
            description:        ni.description ?? null,
            customer_id:        ni.customer_id,
            delivery_address:   ni.delivery_address,
            delivery_method:    ni.delivery_method ?? null,
            delivery_method_id: ni.delivery_method_id ?? null,
            expected_cartons:   ni.expected_cartons,
            expected_weight_kg: ni.expected_weight_kg,
            expected_cbm:       ni.expected_cbm,
            actual_cartons:     0,
            pallet_count:       0,
            pallets_qty:        0,
            pallets_detail:     [],
            condition:          'good',
            split_no:           ni.split_no ?? null,
            system_ref:         ni.system_ref ?? null,
            group_name:         ni.group_name ?? null,
          }))
        )
      }
    }

    if (linkedDev && ['awaiting', 'started'].includes(linkedDev.status)) {
      setSyncConfirmOp({ fn: doSplit })
    } else {
      await doSplit()
    }
  }

  useEffect(() => { fetchPlan() }, [fetchPlan])

  // Shared by both exports so PDF and Excel never drift out of sync with each other.
  const buildSplits = (): PdfSplit[] => {
    const splits: PdfSplit[] = groups
      .filter(g => g.name !== null)
      .map(g => ({ name: g.name!, items: g.items, ctns: g.ctns }))
    if (splits.length === 0) {
      // No named splits — one section with all items
      splits.push({ name: 'All Items', items, ctns: items.reduce((s, i) => s + (i.expected_cartons || 0), 0) })
    }
    return splits
  }

  const handleDownloadPdf = async () => {
    if (!plan) return
    setDownloadingPdf(true)
    try {
      const splits = buildSplits()
      const blob = await pdf(<PreDevanPlanPDF plan={plan} splits={splits} />).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `PreDevan_${plan.container_no}.pdf`
      a.click(); URL.revokeObjectURL(url)
    } finally {
      setDownloadingPdf(false)
    }
  }

  // Z50.2: mirrors PreDevanPlanPDF's layout exactly (same group split as PDF, same
  // JOB REF/SPLIT NO/DESCRIPTION/N/M/POSTCODE/CTNS column set and field derivations,
  // same TOTAL CTNS / GRAND TOTAL CTNS footers) so the two exports never drift apart —
  // see PreDevanPlanPDF above for the field-derivation source of truth.
  //
  // Uses ExcelJS rather than the `xlsx` package used elsewhere in this file: the
  // installed `xlsx` community edition can write merged cells but cannot write cell
  // styles (verified — bold/fill are silently dropped on write), and real bold on the
  // TOTAL CTNS row was an explicit, non-optional part of this ask.
  const handleDownloadExcel = async () => {
    if (!plan) return
    setDownloadingExcel(true)
    try {
      const splits = buildSplits()
      const grandTotal = splits.reduce((s, g) => s + g.ctns, 0)

      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Pre-Devan Plan')
      // Z50.3: Group No (the section's own name, e.g. "Group 1") added as a real data
      // column alongside Split No (the numeric Loading List suffix) — previously Group
      // No only appeared in the header block above each section, not as its own column.
      ws.columns = [
        { key: 'groupNo',  width: 14 },
        { key: 'jobRef',   width: 16 },
        { key: 'splitNo',  width: 10 },
        { key: 'desc',     width: 42 },
        { key: 'nm',       width: 16 },
        { key: 'postcode', width: 12 },
        { key: 'ctns',     width: 10 },
      ]

      const DARK = 'FF1E293B'
      const LIGHT_HDR = 'FFE2E8F0'
      const FOOT_BG = 'FFF8FAFC'
      const WHITE = 'FFF8FAFC'

      const mergeFullWidth = (row: number) => ws.mergeCells(row, 1, row, 7)

      splits.forEach(split => {
        const first = split.items[0]
        const address = first?.delivery_address || '—'
        const contact = first?.delivery_contact || null
        const method  = first?.delivery_method  || null

        // Group header block (name / address / contact / method) — mirrors pdfS.splitHdr
        const nameRow = ws.addRow([split.name])
        mergeFullWidth(nameRow.number)
        nameRow.font = { bold: true, size: 12, color: { argb: WHITE } }
        nameRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } }

        const addrRow = ws.addRow([address])
        mergeFullWidth(addrRow.number)
        addrRow.font = { color: { argb: WHITE }, size: 9 }
        addrRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } }

        if (contact) {
          const contactRow = ws.addRow([`Contact: ${contact}`])
          mergeFullWidth(contactRow.number)
          contactRow.font = { color: { argb: WHITE }, size: 9 }
          contactRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } }
        }
        if (method) {
          const methodRow = ws.addRow([`Method: ${method}`])
          mergeFullWidth(methodRow.number)
          methodRow.font = { color: { argb: WHITE }, size: 9 }
          methodRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } }
        }

        // Column header row
        const colHdrRow = ws.addRow(['GROUP NO', 'JOB REF', 'SPLIT NO', 'DESCRIPTION', 'N/M', 'POSTCODE', 'CTNS'])
        colHdrRow.font = { bold: true, size: 9 }
        colHdrRow.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_HDR } }
        })

        // Data rows — same field derivations as PreDevanPlanPDF's table
        split.items.forEach(item => {
          ws.addRow([
            split.name,
            item.client_ref || item.hbl_no || '—',
            item.split_no || '—',
            item.description || '—',
            item.system_ref || '—',
            item.delivery_postcode || '—',
            item.expected_cartons,
          ])
        })

        // TOTAL CTNS footer — bold, matches pdfS.splitFoot
        const footRow = ws.addRow(['', '', '', '', '', 'TOTAL CTNS', split.ctns])
        footRow.font = { bold: true }
        footRow.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FOOT_BG } }
          cell.border = { top: { style: 'thin', color: { argb: 'FFCBD5E1' } } }
        })

        ws.addRow([]) // blank separator row between groups
      })

      // GRAND TOTAL CTNS — mirrors pdfS.grandFoot on the PDF's last page
      const gtRow = ws.addRow(['', '', '', '', '', 'GRAND TOTAL CTNS', grandTotal])
      gtRow.font = { bold: true, size: 12, color: { argb: WHITE } }
      gtRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } }
      })

      const buffer = await wb.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `PreDevan_${plan.container_no}.xlsx`
      a.click(); URL.revokeObjectURL(url)
    } finally {
      setDownloadingExcel(false)
    }
  }

  async function handleDraftNote(group: GroupEntry, type: 'collection' | 'delivery') {
    if (!plan) return
    // Fetch warehouse address for Collect From field
    const { data: wh } = await supabase.from('warehouses').select('name,address').eq('id', id!).maybeSingle()
    const whAddr = wh ? (typeof wh.address === 'object' && wh.address
      ? Object.values(wh.address as Record<string,string>).filter(Boolean).join(', ')
      : (wh as any).name) : ''

    const first = group.items[0]
    // Compose customer_ref from group items
    const childRefs = [...new Set(group.items.map(it => it.client_ref).filter(Boolean))]
    const customerRef = childRefs.length > 0 ? childRefs.join('+') : null

    const totalCtns = group.items.reduce((s, i) => s + (i.expected_cartons || 0), 0)
    const totalWt   = group.items.reduce((s, i) => s + (i.expected_weight_kg || 0), 0)
    const totalCbm  = group.items.reduce((s, i) => s + (i.expected_cbm || 0), 0)
    // Item remarks (e.g. FBA warehouse names) captured during LL column mapping —
    // one per distinct note, shown as its own line in the PDF Remarks section.
    const itemNotes = [...new Set(group.items.map(i => i.notes).filter(Boolean))] as string[]

    const noteForm: NoteData = {
      company:            await getCompanySettings(),
      note_ref:           `DRAFT-${plan.container_no || plan.id.slice(0, 8)}-${group.name || 'ALL'}`,
      note_type:          type,
      job_ref:            plan.job_ref || null,
      customer_ref:       customerRef,
      planned_release_at: plan.planned_devan_at || null,
      contact_name:       first?.delivery_contact || null,
      contact_phone:      null,
      vehicle_reg:        null,
      driver_name:        null,
      haulier:            null,
      delivery_address:   first?.delivery_address || null,
      collect_from:       whAddr || null,
      marks_numbers:      null,
      container_no:       plan.container_no || null,
      seal_no:            null,
      container_type:     null,
      remarks:            itemNotes.length > 0 ? itemNotes.join('\n') : null,
      delivery_refs:      [],
      notes:              null,
      warehouse_name:     (wh as any)?.name || '',
      issued_at:          new Date().toISOString(),
      // Actual serial_no is assigned by the dispatch_notes_serial_seq DB sequence
      // on insert — filled in from the insert response right before PDF generation.
      serial_no:          null,
    }

    const noteItems: NoteItem[] = [{
      group_name:      group.name,
      description:     group.items.map(i => i.description).filter(Boolean).join(' / '),
      pallets_qty:     null,
      cartons:         totalCtns,
      kgs:             totalWt || null,
      cbm:             totalCbm || null,
      delivery_address: first?.delivery_address || null,
      system_refs:     group.items.map(i => i.system_ref).filter(Boolean).join('\n'),
    }]

    setDraftNote({ form: noteForm, items: noteItems, submitting: false })
  }

  // Extract the split number from "Group N" (or legacy "Devan Split N") — ignores any suffix after —
  const splitSortKey = (name: string) => { const m = name.match(/(?:devan split|group)\s+(\d+)/i); return m ? parseInt(m[1]) : Infinity }

  // Build ordered groups from group_name, sorted by trailing number
  const groups = useMemo<GroupEntry[]>(() => {
    const map = new Map<string, DevanningPlanItem[]>()
    items.forEach(item => {
      const key = item.group_name ?? '__unassigned__'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    })

    const result: GroupEntry[] = []
    for (const [key, groupItems] of map) {
      if (key === '__unassigned__') continue
      result.push({
        name: key,
        items: groupItems,
        ctns: groupItems.reduce((s, i) => s + (i.expected_cartons || 0), 0),
        wt: groupItems.reduce((s, i) => s + (i.expected_weight_kg || 0), 0),
        cbm: groupItems.reduce((s, i) => s + (i.expected_cbm || 0), 0),
      })
    }
    result.sort((a, b) => splitSortKey(a.name!) - splitSortKey(b.name!))
    if (map.has('__unassigned__')) {
      const groupItems = map.get('__unassigned__')!
      result.push({
        name: null,
        items: groupItems,
        ctns: groupItems.reduce((s, i) => s + (i.expected_cartons || 0), 0),
        wt: groupItems.reduce((s, i) => s + (i.expected_weight_kg || 0), 0),
        cbm: groupItems.reduce((s, i) => s + (i.expected_cbm || 0), 0),
      })
    }
    return result
  }, [items])

  const handleRenameGroup = useCallback(async (oldName: string, newName: string) => {
    const conflict = groups.some(g => g.name !== null && g.name !== oldName && g.name === newName)
    if (conflict) { alert(`A split named "${newName}" already exists.`); return }
    const affectedIds = items.filter(it => it.group_name === oldName).map(it => it.id)
    if (!affectedIds.length) return
    const doRename = async () => {
      const { error } = await supabase.from('devanning_plan_items')
        .update({ group_name: newName }).in('id', affectedIds)
      if (error) { alert(error.message); return }
      setItems(prev => prev.map(it => it.group_name === oldName ? { ...it, group_name: newName } : it))
      if (linkedDev?.status === 'awaiting') {
        await supabase.from('devanning_items')
          .update({ group_name: newName }).in('plan_item_id', affectedIds)
      }
    }
    if (linkedDev?.status === 'awaiting') {
      setSyncConfirmOp({ fn: doRename })
    } else {
      await doRename()
    }
  }, [groups, items, linkedDev])

  const totalCartons = useMemo(() => items.reduce((s, i) => s + (i.expected_cartons || 0), 0), [items])
  const hasGroups = groups.some(g => g.name !== null)
  const isReadOnly = !!linkedDev && (linkedDev.status === 'started' || linkedDev.status === 'completed')

  if (!plan) return <WarehouseLayout warehouseId={id!}><div className="py-16 text-center text-gray-400">Loading...</div></WarehouseLayout>

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-6">
        {/* Revert banner */}
        {plan.last_reason && plan.status === 'pending' && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Reverted from Devannings</p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">{plan.last_reason}</p>
            {plan.last_changed_by && <p className="text-xs text-amber-500 mt-0.5">{plan.last_changed_by}{plan.last_changed_at ? ` · ${new Date(plan.last_changed_at).toLocaleString('en-GB')}` : ''}</p>}
          </div>
        )}
        {/* Linked devanning banner */}
        {linkedDev && (linkedDev.status === 'started' || linkedDev.status === 'completed') && (
          <div className="rounded-xl px-4 py-3 border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                🔒 Devanning has {linkedDev.status === 'completed' ? 'completed' : 'started'}. Plan is locked. To change this, go to the Devannings tab and click Revert to Awaiting.
              </p>
              <Link to={`/warehouse/${id}/devannings/${linkedDev.id}`}
                className="text-sm font-semibold text-red-700 dark:text-red-300 hover:underline whitespace-nowrap">
                Go to Devanning →
              </Link>
            </div>
          </div>
        )}
        {linkedDev && linkedDev.status === 'awaiting' && (
          <div className="rounded-xl px-4 py-3 border bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-indigo-800 dark:text-indigo-200">
                Awaiting Devanning — saving changes will prompt sync confirmation.
              </p>
              <Link to={`/warehouse/${id}/devannings/${linkedDev.id}`}
                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap">
                Open Devanning →
              </Link>
            </div>
          </div>
        )}
        {/* No loading list banner */}
        {!plan.source_loading_list_id && plan.job_ref && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl px-4 py-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                  ⚠ Loading List not uploaded for {plan.job_ref}
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
                  Splits and items cannot be created until you upload the Excel.
                </p>
              </div>
              <Link
                to={`/warehouse/${id}/loading-lists/upload?job_ref=${encodeURIComponent(plan.job_ref)}&container_no=${encodeURIComponent(plan.container_no || '')}`}
                className="flex-shrink-0 px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 whitespace-nowrap">
                Upload Loading List →
              </Link>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Link to={`/warehouse/${id}/plans`} className="text-sm text-blue-600 hover:underline block mb-1">← Plans</Link>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
              {planJobRef && <span className="text-gray-400 font-normal mr-1">{planJobRef} ·</span>}
              {plan.container_no}
            </h2>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1">
              {plan.vessel && <span>Vessel: <strong>{plan.vessel}</strong></span>}
              {plan.voyage && <span>Voyage: <strong>{plan.voyage}</strong></span>}
              {plan.carrier && <span>Carrier: <strong>{plan.carrier}</strong></span>}
              {plan.eta_date && <span>ETA: <strong>{fmtDate(plan.eta_date)}</strong></span>}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-sm px-3 py-1 rounded-full font-medium ${
              plan.status === 'pending' ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'
            }`}>{plan.status}</span>
            {plan.source_loading_list_id && (
              <div className="flex items-center gap-2">
                <button onClick={handleSyncFromLL} disabled={syncingLL}
                  title="Update plan items with current loading list data"
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50">
                  <svg className={`w-4 h-4 ${syncingLL ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {syncingLL ? 'Syncing…' : 'Sync from LL'}
                </button>
                {syncResult && (
                  <span className={`text-xs font-medium ${syncResult.startsWith('Sync failed') ? 'text-red-600' : 'text-green-600'}`}>
                    {syncResult}
                  </span>
                )}
              </div>
            )}
            <button onClick={handleDownloadPdf} disabled={downloadingPdf}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3" />
              </svg>
              {downloadingPdf ? 'Generating…' : 'Download PDF'}
            </button>
            <button onClick={handleDownloadExcel} disabled={downloadingExcel}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3" />
              </svg>
              {downloadingExcel ? 'Generating…' : 'Download Excel'}
            </button>
            {plan.status === 'pending' && !linkedDev && (
              <button onClick={() => setShowMoveToPlanned(true)}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Move to Planned →
              </button>
            )}
            {linkedDev && (
              <Link to={`/warehouse/${id}/devannings/${linkedDev.id}`} className="text-sm text-blue-600 hover:underline">
                Open Devanning →
              </Link>
            )}
          </div>
        </div>

        {/* Summary bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center gap-8">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Devan Splits</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{hasGroups ? groups.filter(g => g.name !== null).length : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Total CTNS</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white font-mono">{totalCartons}</p>
          </div>
        </div>

        {/* Groups list */}
        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading...</div>
        ) : hasGroups ? (
          <div className="space-y-3">
            <div className="flex justify-end">
              <ColumnToggleButton cols={PLAN_ITEMS_COL_DEFS} hidden={piHidden} onToggle={piToggleHidden} onReset={piReset}
                onRecalculate={() => { const w = computeAutoFitWidths(PLAN_ITEMS_COL_DEFS); setPiExtra({ ...piExtra, widths: { ...w, ...piSavedWidths } }) }} />
            </div>
            {selectedItems.size > 0 && !isReadOnly && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                </span>
                <div className="relative">
                  <button onClick={() => setBulkMoveOpen(p => !p)}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Move to…
                  </button>
                  {bulkMoveOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setBulkMoveOpen(false)} />
                      <div className="absolute left-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl py-1 min-w-[160px] max-h-56 overflow-y-auto">
                        {groups.filter(g => g.name !== null).map(g => (
                          <button key={g.name} type="button" onClick={() => handleBulkMove(g.name)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {g.name}
                          </button>
                        ))}
                        <button type="button" onClick={() => handleBulkMove(null)}
                          className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                          Unassigned
                        </button>
                        <div className="border-t border-gray-100 dark:border-gray-700 my-0.5" />
                        <button type="button" onClick={() => {
                          const names = groups.filter(g => g.name !== null).map(g => g.name!)
                          const max = names.reduce((m, n) => Math.max(m, parseInt(n.match(/(\d+)$/)?.[1] ?? '0')), 0)
                          handleBulkMove(`Devan Split ${max + 1}`)
                        }}
                          className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                          + New Split
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={() => setSelectedItems(new Set())} className="ml-auto text-xs text-blue-600 hover:underline">
                  Clear
                </button>
              </div>
            )}
            {groups.map((g, i) => (
              <GroupCard
                key={g.name ?? '__unassigned__'}
                group={g}
                companies={companies}
                onUpdateAgent={updateAgent}
                index={i}
                onEdit={item => setModalState({ type: 'edit', item })}
                onDelete={handleDeleteItem}
                onSplit={item => setModalState({ type: 'split', item })}
                onAdd={groupName => setModalState({ type: 'add', groupName })}
                allGroupNames={groups.filter(g2 => g2.name !== null).map(g2 => g2.name as string)}
                selectedItems={selectedItems}
                onToggleSelect={handleToggleSelect}
                onMoveItem={handleMoveItem}
                isReadOnly={isReadOnly}
                itemVisibleCols={piVisibleCols}
                itemColOrder={piColOrder}
                itemSensors={piSensors}
                handleItemColDragEnd={handlePiColDragEnd}
                getColW={getPiColW}
                onResizeEnd={savePiColW}
                onResizeReset={resetPiColW}
                onRename={handleRenameGroup}
                onSelectAll={handleSelectAll}
                onDraftNote={handleDraftNote}
              />
            ))}
          </div>
        ) : !plan.source_loading_list_id && plan.job_ref && items.length === 0 ? (
          /* No LL linked — upload prompt */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-6 py-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">No data — upload Loading List to populate splits and items</p>
            <p className="text-gray-400 text-xs mb-4">{plan.job_ref} · {plan.container_no}</p>
            <Link
              to={`/warehouse/${id}/loading-lists/upload?job_ref=${encodeURIComponent(plan.job_ref)}&container_no=${encodeURIComponent(plan.container_no || '')}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Loading List
            </Link>
          </div>
        ) : (
          /* No groups — flat table fallback */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center gap-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">Plan Items ({items.length})</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Total: <strong>{totalCartons}</strong> cartons</span>
                <ColumnToggleButton
                  cols={PLAN_ITEMS_COL_DEFS}
                  hidden={piHidden}
                  onToggle={piToggleHidden}
                  onReset={piReset}
                  onRecalculate={() => { const w = computeAutoFitWidths(PLAN_ITEMS_COL_DEFS); setPiExtra({ ...piExtra, widths: { ...w, ...piSavedWidths } }) }}
                />
                {!isReadOnly && (
                  <button type="button" onClick={() => setModalState({ type: 'add', groupName: null })}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Item
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto" {...piScrollProps}>
              <table ref={piTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <DndContext sensors={piSensors} collisionDetection={closestCenter} onDragEnd={handlePiColDragEnd}>
                  <SortableContext items={piColOrder} strategy={horizontalListSortingStrategy}>
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">#</th>
                        {piVisibleCols.map(col => (
                          <SortableColHeader key={col.id} id={col.id}
                            style={{ width: getPiColW(col.id), minWidth: '50px' }}
                            className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                            onResizeEnd={w => savePiColW(col.id, w)}
                            onResizeReset={() => resetPiColW(col.id)}>
                            {col.label}
                          </SortableColHeader>
                        ))}
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                  </SortableContext>
                </DndContext>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map((item, i) => {
                    const flatCells: Record<string, React.ReactNode> = {
                      hbl:     <td key="hbl"     className="px-3 py-2 font-mono text-xs">{item.hbl_no || '—'}</td>,
                      job_ref: <td key="job_ref" className="px-3 py-2 font-mono text-xs text-blue-700 dark:text-blue-400">{item.client_ref || '—'}</td>,
                      desc:    <td key="desc"    className="px-3 py-2 text-xs max-w-[180px] truncate">{item.description || '—'}</td>,
                      nm:      <td key="nm"      className="px-3 py-2 text-xs text-gray-500 font-mono">{item.system_ref || '—'}</td>,
                      method:  <td key="method"  className="px-3 py-2 text-xs text-gray-500">{item.delivery_method || '—'}</td>,
                      ctns:    <td key="ctns"    className="px-3 py-2 text-center font-mono">{item.expected_cartons}</td>,
                      wt:      <td key="wt"      className="px-3 py-2 text-center text-gray-500">{item.expected_weight_kg ?? '—'}</td>,
                      cbm:     <td key="cbm"     className="px-3 py-2 text-center text-gray-500">{item.expected_cbm ?? '—'}</td>,
                      address: <td key="address" className="px-3 py-2 text-xs text-gray-500 max-w-[200px] break-words">{item.delivery_address || '—'}</td>,
                    }
                    return (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                      {piVisibleCols.map(col => flatCells[col.id])}
                      <td className="px-2 py-1 whitespace-nowrap">
                        {!isReadOnly && (
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => setModalState({ type: 'edit', item })} title="Edit"
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-blue-600">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
                              </svg>
                            </button>
                            <button type="button" onClick={() => setModalState({ type: 'split', item })} title="Split">
                              <span className="text-xs text-gray-400 hover:text-indigo-600 px-0.5">✂</span>
                            </button>
                            <button type="button" onClick={() => handleDeleteItem(item)} title="Delete"
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-600">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                  })}
                  {items.length === 0 && (
                    <tr><td colSpan={piVisibleCols.length + 2} className="px-4 py-10 text-center text-gray-400">No items in this plan.</td></tr>
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot className="bg-gray-50 dark:bg-gray-700 text-sm font-semibold">
                    <tr>
                      <td className="px-3 py-2 text-right text-gray-500">Totals</td>
                      {piVisibleCols.map(col => {
                        if (col.id === 'ctns')    return <td key="ctns" className="px-3 py-2 text-center font-mono">{totalCartons}</td>
                        if (col.id === 'wt')      return <td key="wt"   className="px-3 py-2 text-center">{items.reduce((s, i) => s + (i.expected_weight_kg || 0), 0).toFixed(1)}</td>
                        if (col.id === 'cbm')     return <td key="cbm"  className="px-3 py-2 text-center">{items.reduce((s, i) => s + (i.expected_cbm || 0), 0).toFixed(3)}</td>
                        return <td key={col.id} />
                      })}
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
      <StickyHScrollbar {...piScrollBar} />

      {showMoveToPlanned && (
        <MoveToPlannedDialog
          plan={plan}
          jobRef={planJobRef}
          warehouseId={id!}
          onDone={devId => {
            setShowMoveToPlanned(false)
            setPlan(prev => prev ? { ...prev, status: 'planned' } : prev)
            navigate(`/warehouse/${id}/devannings/${devId}`)
          }}
          onClose={() => setShowMoveToPlanned(false)}
        />
      )}

      {modalState && modalState.type !== 'split' && (
        <PlanItemModal
          item={modalState.item ?? null}
          groupName={modalState.groupName ?? null}
          onSave={handleSaveItem}
          onClose={() => setModalState(null)}
        />
      )}

      {modalState?.type === 'split' && modalState.item && (
        <PlanItemSplitModal
          item={modalState.item}
          onSplit={(a, b) => handleSplitItem(modalState.item!, a, b)}
          onClose={() => setModalState(null)}
        />
      )}

      {syncConfirmOp && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-5">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">Sync to Awaiting Devanning?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                Pre-Devanning Plan modified. Confirm to sync changes to the awaiting devanning items. Cancel to discard the plan change.
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setSyncConfirmOp(null)}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Cancel changes
                </button>
                <button onClick={async () => {
                  const op = syncConfirmOp
                  setSyncConfirmOp(null)
                  try { await op.fn() } catch (err: any) { alert(err.message || 'Save failed') }
                }}
                  className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Confirm sync
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Draft Collection / Delivery Note Modal ─────────────────────────── */}
      {draftNote && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Draft {draftNote.form.note_type === 'collection' ? 'Collection' : 'Delivery'} Note
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">All fields are pre-filled from plan data — edit freely. Blank fields can be completed after devanning.</p>
              </div>
              <button onClick={() => setDraftNote(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
              {([
                ['note_ref',           'Note Ref'],
                ['job_ref',            'Job Reference'],
                ['customer_ref',       'Customer Ref'],
                ['collect_from',       'Collect From (warehouse address)'],
                ['delivery_address',   draftNote.form.note_type === 'collection' ? 'Release To Address' : 'Deliver To Address'],
                ['planned_release_at', 'Collection Date'],
                ['contact_name',       'Contact Name'],
                ['contact_phone',      'Contact Phone'],
                ['vehicle_reg',        'Vehicle Reg'],
                ['driver_name',        'Driver Name'],
                ['haulier',            'Haulier / Carrier'],
                ['marks_numbers',      'Marks & Numbers (MBL)'],
                ['container_no',       'Container No'],
                ['seal_no',            'Seal No'],
                ['container_type',     'Container Type (e.g. 40ft HC)'],
                ['remarks',            'Remarks'],
                ['notes',              'Notes'],
              ] as [keyof NoteData, string][]).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
                  {field === 'notes' || field === 'remarks' || field === 'collect_from' || field === 'delivery_address' || field === 'customer_ref' ? (
                    <textarea rows={2} className={inputCls}
                      value={(draftNote.form[field] as string) || ''}
                      onChange={e => setDraftNote(d => d ? { ...d, form: { ...d.form, [field]: e.target.value || null } } : d)} />
                  ) : (
                    <input className={inputCls}
                      type={field === 'planned_release_at' ? 'datetime-local' : 'text'}
                      value={field === 'planned_release_at'
                        ? ((draftNote.form[field] as string)?.slice(0, 16) || '')
                        : ((draftNote.form[field] as string) || '')}
                      onChange={e => setDraftNote(d => d ? { ...d, form: { ...d.form, [field]: e.target.value || null } } : d)} />
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery REF codes (one per line, per child job)</label>
                <textarea rows={3} className={inputCls}
                  value={draftNote.form.delivery_refs.join('\n')}
                  onChange={e => setDraftNote(d => d ? { ...d, form: { ...d.form, delivery_refs: e.target.value.split('\n').filter(Boolean) } } : d)}
                  placeholder="e.g. O48469763W=YY202605250481" />
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-4">
              <button onClick={() => setDraftNote(null)} className={btnSecondary}>Cancel</button>
              <button
                disabled={draftNote.submitting}
                onClick={async () => {
                  if (!plan) return
                  setDraftNote(d => d ? { ...d, submitting: true } : d)
                  try {
                    // Save to dispatch_notes — serial_no is assigned by the DB sequence default
                    const { data: inserted, error: insErr } = await supabase.from('dispatch_notes').insert({
                      plan_id:      plan.id,
                      warehouse_id: id!,
                      group_name:   draftNote.form.note_ref.split('-').slice(2).join('-') || null,
                      note_type:    draftNote.form.note_type,
                      status:       'draft',
                      draft_data:   draftNote.form,
                      created_by:   user?.email || null,
                    }).select('serial_no').single()
                    if (insErr) throw insErr
                    const noteForPdf = { ...draftNote.form, serial_no: inserted.serial_no }
                    // Generate + download PDF
                    const blob = await pdf(<NotePDF note={noteForPdf} items={draftNote.items} />).toBlob()
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    const postcode = draftNote.form.delivery_address?.match(/[A-Z]{1,2}\d[0-9A-Z]?\s*\d[A-Z]{2}/i)?.[0]?.replace(' ', '') || 'draft'
                    a.href = url; a.download = `${plan.job_ref || 'note'}_${postcode}_${draftNote.form.note_type}_note.pdf`
                    a.click(); URL.revokeObjectURL(url)
                    setDraftNote(null)
                  } catch (err: any) {
                    alert(`Error: ${err.message}`)
                    setDraftNote(d => d ? { ...d, submitting: false } : d)
                  }
                }}
                className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
                {draftNote.submitting ? 'Generating…' : `Save Draft & Download PDF`}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
